// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import { THLayout } from '@Molecules/THLayout';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import { THPassword } from '@Molecules/Inputs/THPassword/THPassword';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THNumberSelect } from '@Molecules/Inputs/THNumberSelect/THNumberSelect';
import { useSMTPConfigForm } from './useSMTPConfigForm';
import { NAME_KEY } from './constant';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { classList } from '@Helpers/className';

import './MailerDetails.scss';

export const MailerDetails = () => {
    const i = useLocalMessage();
    const {
        form,
        onChangeForm,
        onResetForm,
        initialValues,
        onFinishForm,
        isValid,
        fieldsTouched,
        isTls,
        onChangePort,
        onChangeSecurity,
        handleTestConfiguration,
    } = useSMTPConfigForm();

    return (
        <THLayout>
            <THLayout.Content className="MailerLayout">
                <THForm
                    form={form}
                    autoComplete="off"
                    onFinish={onFinishForm}
                    initialValues={initialValues}
                    onValuesChange={onChangeForm}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                warning
                                disabled={!isValid}
                                onCancel={onResetForm}
                                hidden={!fieldsTouched}
                            />
                        }
                    >
                        {/* Server settings */}
                        <div className="MailerDetails__Settings">
                            <TitleSection>{i('smtp.settings')}</TitleSection>
                            <div className="input-half MailerDetails__SettingsHostAndPort">
                                <THForm.Item
                                    name={NAME_KEY.host}
                                    label={i('smtp.fields.host')}
                                    rules={[{ required: true, message: i('smtp.required.host') }]}
                                    className="flex-item-1"
                                >
                                    <THInput />
                                </THForm.Item>

                                <THForm.Item
                                    name={NAME_KEY.port}
                                    label={i('smtp.fields.port')}
                                    className="portField"
                                    rules={[{ required: true, message: i('smtp.required.port') }]}
                                >
                                    <THInputNumber onChange={onChangePort} />
                                </THForm.Item>
                            </div>

                            <THForm.Item
                                name={NAME_KEY.from}
                                rules={[{ required: true, type: 'email', message: i('smtp.required.from') }]}
                                label={i('smtp.fields.from')}
                                className="input-half"
                            >
                                <THInput />
                            </THForm.Item>
                        </div>

                        {/* Authentication & security */}
                        <div className="MailerDetails__Connection">
                            <TitleSection>{i('smtp.connexion')}</TitleSection>

                            <div className="input-half MailerDetails__ConnectionSecurity">
                                <THForm.Item
                                    label={i('smtp.fields.security')}
                                    name={NAME_KEY.security}
                                    className="flex-item-1"
                                >
                                    <THSelect
                                        options={[
                                            { label: i('smtp.fields.none'), value: 'none' },
                                            { label: i('smtp.fields.ssl'), value: 'ssl' },
                                            { label: i('smtp.fields.starttls'), value: 'tls' },
                                        ]}
                                        onChange={onChangeSecurity}
                                    />
                                </THForm.Item>
                                <THForm.Item
                                    name={NAME_KEY.tlsRequired}
                                    hidden={!isTls}
                                    label={i('smtp.fields.tlsRequired')}
                                    valuePropName="checked"
                                    className="MailerDetails__security-tlsrequired"
                                >
                                    <THSwitch />
                                </THForm.Item>
                            </div>

                            <div className={classList('input-half', 'MailerDetails__ConnectionCredentials')}>
                                <THForm.Item name={NAME_KEY.user} label={i('smtp.fields.user')} className="input-half">
                                    <THInput autoComplete="new-password" />
                                </THForm.Item>

                                <THForm.Item
                                    name={NAME_KEY.password}
                                    label={i('smtp.fields.password')}
                                    className="input-half"
                                >
                                    <THPassword autoComplete="new-password" />
                                </THForm.Item>
                            </div>

                            <THForm.Item shouldUpdate>
                                {({ getFieldsError }) => {
                                    const [
                                        { errors: hostErrors = [] },
                                        { errors: portErrors = [] },
                                        { errors: fromErrors = [] },
                                    ] = getFieldsError([NAME_KEY.host, NAME_KEY.port, NAME_KEY.from]) || [];

                                    return (
                                        <THButton
                                            disabled={!!hostErrors.length || !!portErrors.length || !!fromErrors.length}
                                            onClick={handleTestConfiguration}
                                        >
                                            {i('smtp.testConfiguration')}
                                        </THButton>
                                    );
                                }}
                            </THForm.Item>
                        </div>

                        {/* Reset Password */}
                        <div className="MailerDetails__ResetPassword">
                            <TitleSection>{i('smtp.resetPassword')}</TitleSection>

                            <THForm.Item
                                name={NAME_KEY.expiration}
                                label={i('smtp.fields.tokenExpiration')}
                                className="input-half"
                            >
                                <THNumberSelect options={['seconds', 'minutes', 'hours']} />
                            </THForm.Item>
                        </div>
                    </THForm.Layout>
                </THForm>
            </THLayout.Content>
        </THLayout>
    );
};
